import { getEnumMemberName } from 'utils/helpers'
import { augmentIncomingCreativeMedia } from 'components/Recommendations/Campaign/Media/mediaPayload'
import { parseOrRaw } from 'domain/shared/validation'
import { setupProcessResponseSchema, setupProcessPreviewResponseSchema } from './schemas'

const DEFAULT_AGE_RANGE = Object.freeze({ min: 18, max: 65 })

// integration_details now carries entries for multiple platforms (facebook,
// google) — a lookup keyed on asset_class alone would silently collapse two
// platforms' entries into one. Callers must say which platform they mean.
export function findIntegrationDetail(integrationDetails, { platform, assetClass }) {
  return (integrationDetails ?? []).find(
    (x) => x.platform === platform && x.asset_class === assetClass,
  )
}

// Plural counterpart of findIntegrationDetail — Google's conversion-action
// "category" selection is multi-valued (one entry per selected goal, all
// sharing the same platform+asset_class), unlike every other asset_class
// today, which is 1:1.
export function findIntegrationDetails(integrationDetails, { platform, assetClass }) {
  return (integrationDetails ?? []).filter(
    (x) => x.platform === platform && x.asset_class === assetClass,
  )
}

// Google's own asset_class value for a conversion-goal selection (confirmed
// against fd-client-api-google-ads: app/model/support/integration_enum.py's
// AssetClass enum + the release-validation write-path tests in
// tests/release_validation/boost_google_publish_conversion_goals/) — distinct
// from Meta's 'conversion', since the data shapes differ (single scalar vs.
// mode + resource_name(s)).
export const GOOGLE_CONVERSION_ACTION_ASSET_CLASS = 'conversion_action'

// Replaces any existing entry with the same (platform, asset_class) rather
// than appending a duplicate — needed because callers rebuild this array on
// every save (fetch -> edit -> save), and asset_class alone isn't a unique
// key once multiple platforms are present. Passing a falsy asset_id removes
// the entry instead of writing one (e.g. "no lead form selected").
export function upsertIntegrationDetail(integrationDetails, entry) {
  const details = (integrationDetails ?? []).filter(
    (x) => !(x.platform === entry.platform && x.asset_class === entry.asset_class),
  )
  if (entry.asset_id) {
    details.push(entry)
  }
  return details
}

// upsertIntegrationDetail replaces exactly one entry per (platform,
// asset_class) — correct for Meta's scalar selections, but Google's
// conversion-goal "category" mode can select N sibling conversion_action
// entries at once (one per selected goal). Replaces the whole group instead
// of a single entry so callers don't have to fold N entries through N
// upsertIntegrationDetail calls (which would just keep clobbering each
// other, since none of them are individually addressable by platform+
// asset_class alone).
export function replaceIntegrationDetailGroup(
  integrationDetails,
  { platform, assetClass },
  entries,
) {
  const details = (integrationDetails ?? []).filter(
    (x) => !(x.platform === platform && x.asset_class === assetClass),
  )
  return [...details, ...entries]
}

function buildDefaultTargeting(creatives) {
  const tcs = (creatives ?? []).map((x) => x.tc)
  return tcs.map((tc) => ({ tc, fb: { age_range: { ...DEFAULT_AGE_RANGE } } }))
}

// Exported for reuse by domain/publishedCampaignSetups, whose taste_clusters[].creative
// is the same unified creative shape as this module's setup_process creatives.
export function mapCreative(creative) {
  // Distinguishes a user-set destination_url from one merely inherited from
  // another TC, so propagation logic doesn't clobber it on the next edit.
  const hasNonEmptyUrl =
    Array.isArray(creative.destination_url) && creative.destination_url.filter(Boolean).length > 0
  const mergedMedia = augmentIncomingCreativeMedia({
    media: creative.media ?? [],
    placement_media: creative.placement_media ?? [],
  })

  return {
    ...creative,
    media: mergedMedia,
    placement_media: [],
    excluded_variations: creative.excluded_variations ?? [],
    ...(hasNonEmptyUrl ? { destination_url_inherited: false } : {}),
  }
}

// v4 response -> FE domain model, replacing the old (v3, flat-targeting)
// parseSetupProcess. Per-TC targeting is exposed as-received, in the
// platform-nested {tc, tc_run_id, fb, google} shape — no more flattening into
// targetingMode/postalCodes/rangeLocations/ageRange.
export function mapSetupProcessResponse(data, eventId) {
  const source = parseOrRaw(setupProcessResponseSchema, data)

  const targeting = source.targeting?.length
    ? source.targeting
    : buildDefaultTargeting(source.creatives)

  // conversion_id/lead_form_id/custom_event_type are no longer flat v4
  // response fields — derived here from integration_details, and scoped to
  // 'facebook' permanently: a Meta selection is one scalar per asset class,
  // while Google's conversion-goal selection is N conversion_action entries
  // (see SelectAdPlatformForm's onGoogleConversionGoalChanged), which reads
  // and writes integration_details directly. Do the same for any new
  // multi-platform lookup instead of adding another scalar.
  const conversion = findIntegrationDetail(source.integration_details, {
    platform: 'facebook',
    assetClass: 'conversion',
  })
  const leadForm = findIntegrationDetail(source.integration_details, {
    platform: 'facebook',
    assetClass: 'lead_form',
  })
  const customEventType = findIntegrationDetail(source.integration_details, {
    platform: 'facebook',
    assetClass: 'custom_event_type',
  })

  return {
    ...source,
    goal: getEnumMemberName(source.goal),
    creatives: (source.creatives ?? []).map(mapCreative),
    targeting,
    conversion_id: conversion?.asset_id,
    lead_form_id: leadForm?.asset_id,
    custom_event_type: customEventType?.asset_id,
    eid: eventId,
  }
}

// v4 GET /setup_processes/{eid}/preview -> FE domain model. Dropped from the
// main setup_process_serializer (see mapSetupProcessResponse) since it's now
// served by its own endpoint, but the FE field name stays the same so callers
// migrating off setup.budget_in_ad_account_currency only need a new data
// source, not a new field name.
export function mapSetupProcessPreviewResponse(data) {
  const source = parseOrRaw(setupProcessPreviewResponseSchema, data)

  return {
    budget_in_ad_account_currency: source.budget_in_ad_account_currency ?? null,
  }
}

// Reconfigure-time conversion-tracking field resolution. Lead form requires
// utilization goal; adding/removing one flips custom_event_type between
// 'LEAD' and 'PURCHASE'. All three are Meta-only — once Meta is deselected
// they must all resolve to null or buildIntegrationDetails re-inserts a
// stale facebook entry on every save.
export function resolveConversionTrackingFields({
  setup,
  config,
  isUtilizationGoal,
  includesMeta = true,
}) {
  if (!includesMeta) {
    return { leadFormId: null, conversionId: null, customEventType: null }
  }

  let leadFormId = 'lead_form_id' in config ? config.lead_form_id : setup.lead_form_id
  let conversionId = 'conversion_id' in config ? config.conversion_id : setup.conversion_id
  let customEventType =
    'custom_event_type' in config ? config.custom_event_type : setup.custom_event_type || 'PURCHASE'

  // Lead form only applies to the utilization goal.
  if (!isUtilizationGoal) {
    leadFormId = null
  }

  const hadLeadForm = !!setup.lead_form_id
  const hasLeadForm = !!leadFormId

  if (!hasLeadForm && hadLeadForm) {
    // Lead form removed
    conversionId = null
    customEventType = 'PURCHASE'
  } else if (hasLeadForm && !hadLeadForm) {
    // Lead form added
    customEventType = 'LEAD'
  }

  if (!customEventType) {
    // make sure custom_event_type is always set
    customEventType = hasLeadForm ? 'LEAD' : 'PURCHASE'
  }

  return { leadFormId, conversionId, customEventType }
}

export function getDisabledVariationsFromCreatives(creatives) {
  return [creatives].flat().reduce((acc, c) => {
    acc[c.tc] = c.excluded_variations || []
    return acc
  }, {})
}
